import type { BatchSubmissionResultV2, BatchSubmissionV2 } from './batchContract';
import type { CorrectionResultV2, CorrectionSubmissionV2 } from './correctionContract';
import { LE_V2_STUDY_DELIVERY_CONTRACT_VERSION, type StudyDeliveryV2, type StudySessionRuntimeV2 } from './studyDeliveryContract';
import { LE_V2_STUDY_RESUME_CONTRACT_VERSION, type StudyResumeV2 } from './studyResumeContract';

export type RpcErrorLike = {
  code?: string;
  message: string;
  details?: string;
  hint?: string;
};

export type LearningEngineV2RpcClient = {
  rpc: (
    functionName: string,
    args: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: RpcErrorLike | null }>;
};

export class LearningEngineV2RpcError extends Error {
  readonly code?: string;
  readonly details?: string;
  readonly hint?: string;

  constructor(operation: string, error: RpcErrorLike) {
    super(`${operation}: ${error.message}`);
    this.name = 'LearningEngineV2RpcError';
    this.code = error.code;
    this.details = error.details;
    this.hint = error.hint;
  }
}

function requireObject(value: unknown, operation: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`${operation}: server returned a non-object payload.`);
  }
  return value as Record<string, unknown>;
}

function assertBatchResult(value: unknown): BatchSubmissionResultV2 {
  const result = requireObject(value, 'submit_study_batch_v2');
  if (result.schemaVersion !== 2 || result.contractVersion !== 'le-v2-04.1') {
    throw new Error('submit_study_batch_v2: incompatible response contract.');
  }
  if (!['completed', 'correction_required', 'review_required', 'duplicate', 'rejected'].includes(String(result.status))) {
    throw new Error('submit_study_batch_v2: unknown response status.');
  }
  return result as BatchSubmissionResultV2;
}

function assertCorrectionResult(value: unknown): CorrectionResultV2 {
  const result = requireObject(value, 'submit_study_correction_v2');
  if (result.schemaVersion !== 2 || result.contractVersion !== 'le-v2-05.1') {
    throw new Error('submit_study_correction_v2: incompatible response contract.');
  }
  if (!['completed', 'correction_required', 'duplicate'].includes(String(result.status))) {
    throw new Error('submit_study_correction_v2: unknown response status.');
  }
  return result as CorrectionResultV2;
}


function assertStudyDelivery(value: unknown): StudyDeliveryV2 {
  const result = requireObject(value, 'get_study_delivery_v2');
  if (result.schemaVersion !== 2 || result.contractVersion !== LE_V2_STUDY_DELIVERY_CONTRACT_VERSION) {
    throw new Error('get_study_delivery_v2: incompatible response contract.');
  }
  if (!Array.isArray(result.questions) || result.questions.length === 0) {
    throw new Error('get_study_delivery_v2: delivery contains no questions.');
  }
  return result as StudyDeliveryV2;
}


function assertStudyResume(value: unknown): StudyResumeV2 {
  const result = requireObject(value, 'get_study_resume_v2');
  if (result.schemaVersion !== 2 || result.contractVersion !== LE_V2_STUDY_RESUME_CONTRACT_VERSION) {
    throw new Error('get_study_resume_v2: incompatible response contract.');
  }
  if (!['new', 'resume_study', 'correction', 'await_review', 'completed'].includes(String(result.mode))) {
    throw new Error('get_study_resume_v2: unknown resume mode.');
  }
  if (!Array.isArray(result.remainingQuestionInstanceIds)) {
    throw new Error('get_study_resume_v2: invalid correction target list.');
  }
  if (result.mode === 'resume_study') {
    if (typeof result.learningSessionId !== 'string' || result.clientBatchSubmissionId !== null || result.remainingQuestionInstanceIds.length !== 0) {
      throw new Error('get_study_resume_v2: incomplete study resume state.');
    }
  }
  if (result.mode === 'correction') {
    if (typeof result.learningSessionId !== 'string' || typeof result.clientBatchSubmissionId !== 'string' || result.remainingQuestionInstanceIds.length === 0) {
      throw new Error('get_study_resume_v2: incomplete correction resume state.');
    }
  }
  return result as StudyResumeV2;
}

function assertStudySessionRuntime(value: unknown): StudySessionRuntimeV2 {
  const result = requireObject(value, 'touch_learning_session_v1');
  if (typeof result.sessionId !== 'string' || typeof result.assignmentId !== 'string') {
    throw new Error('touch_learning_session_v1: incompatible session response.');
  }
  return result as StudySessionRuntimeV2;
}

export async function getStudyDeliveryV2(
  client: LearningEngineV2RpcClient,
  assignmentId: string,
): Promise<StudyDeliveryV2> {
  const { data, error } = await client.rpc('get_study_delivery_v2', { p_assignment_id: assignmentId });
  if (error) throw new LearningEngineV2RpcError('get_study_delivery_v2', error);
  return assertStudyDelivery(data);
}


export async function getStudyResumeV2(
  client: LearningEngineV2RpcClient,
  assignmentId: string,
): Promise<StudyResumeV2> {
  const { data, error } = await client.rpc('get_study_resume_v2', { p_assignment_id: assignmentId });
  if (error) throw new LearningEngineV2RpcError('get_study_resume_v2', error);
  return assertStudyResume(data);
}

export async function startStudySessionV2(
  client: LearningEngineV2RpcClient,
  input: { clientSessionId: string; assignmentId: string; deviceInstanceId?: string | null },
): Promise<StudySessionRuntimeV2> {
  const { data, error } = await client.rpc('touch_learning_session_v1', {
    p_client_session_id: input.clientSessionId,
    p_assignment_id: input.assignmentId,
    p_question_instance_id: null,
    p_status: 'started',
    p_elapsed_duration_ms: 0,
    p_active_duration_ms: 0,
    p_paused_duration_ms: 0,
    p_device_instance_id: input.deviceInstanceId ?? null,
  });
  if (error) throw new LearningEngineV2RpcError('touch_learning_session_v1', error);
  return assertStudySessionRuntime(data);
}

export async function submitStudyBatchV2(
  client: LearningEngineV2RpcClient,
  request: BatchSubmissionV2,
): Promise<BatchSubmissionResultV2> {
  const { data, error } = await client.rpc('submit_study_batch_v2', {
    p_client_batch_submission_id: request.clientBatchSubmissionId,
    p_learning_session_id: request.studySessionId,
    p_assignment_id: request.assignmentId,
    p_worksheet_version_id: request.worksheetVersionId,
    p_submitted_at: request.submittedAt,
    p_elapsed_duration_ms: request.telemetry.elapsedDurationMs,
    p_active_duration_ms: request.telemetry.activeDurationMs,
    p_paused_duration_ms: request.telemetry.pausedDurationMs,
    p_items: request.items,
  });

  if (error) throw new LearningEngineV2RpcError('submit_study_batch_v2', error);
  return assertBatchResult(data);
}

export async function submitStudyCorrectionV2(
  client: LearningEngineV2RpcClient,
  request: CorrectionSubmissionV2,
): Promise<CorrectionResultV2> {
  const { data, error } = await client.rpc('submit_study_correction_v2', {
    p_client_correction_submission_id: request.clientCorrectionSubmissionId,
    p_client_batch_submission_id: request.clientBatchSubmissionId,
    p_learning_session_id: request.studySessionId,
    p_assignment_id: request.assignmentId,
    p_submitted_at: request.submittedAt,
    p_items: request.items,
  });

  if (error) throw new LearningEngineV2RpcError('submit_study_correction_v2', error);
  return assertCorrectionResult(data);
}
